import React, { useEffect } from "react";
import { X } from "lucide-react";

function extractYouTubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
}

/**
 * Fullscreen video overlay — click backdrop or × to close.
 * Supports YouTube (auto-embed) and direct MP4/WebM/MOV URLs.
 */
export default function FullscreenVideo({ open, onClose, url, title = "Video Tutorial" }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const yt = extractYouTubeId(url);

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center animate-fade-in"
      onClick={onClose}
      data-testid="fullscreen-video"
    >
      {/* Close button */}
      <button
        onClick={onClose}
        aria-label="Close video"
        data-testid="fullscreen-video-close"
        className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 backdrop-blur-md hover:bg-white/20 text-white flex items-center justify-center transition active:scale-90 border border-white/20"
      >
        <X className="w-6 h-6" strokeWidth={2.5} />
      </button>

      {/* Title */}
      <div className="absolute top-4 left-4 right-16 text-white font-display font-black text-base tracking-wide drop-shadow-lg truncate">
        {title}
      </div>

      {/* Video */}
      <div
        className="w-full max-w-5xl aspect-video mx-4 rounded-xl overflow-hidden shadow-2xl bg-black"
        onClick={(e) => e.stopPropagation()}
      >
        {!url ? (
          <div className="w-full h-full flex flex-col items-center justify-center text-white/60">
            <div className="text-lg font-bold">Video coming soon</div>
            <div className="text-xs mt-1">Admin ne abhi video upload nahi kiya</div>
          </div>
        ) : yt ? (
          <iframe
            src={`https://www.youtube.com/embed/${yt}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
            title={title}
            className="w-full h-full"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            data-testid="fullscreen-video-iframe"
          />
        ) : (
          <video
            src={url}
            autoPlay
            controls
            playsInline
            className="w-full h-full object-contain bg-black"
            data-testid="fullscreen-video-player"
          />
        )}
      </div>

      {/* Hint */}
      <div className="absolute bottom-6 left-0 right-0 text-center text-white/60 text-xs pointer-events-none">
        Tap anywhere outside to close
      </div>
    </div>
  );
}
